import React, { useState, useEffect, useCallback, useRef } from "react";
import { Building2, Microscope, BadgeCheck, Loader2, TrendingUp, TrendingDown, Sparkles, ShieldCheck, Bitcoin, LineChart, Gem, AlertTriangle, ArrowRight, Zap, Power } from "lucide-react";
import { houseRun, houseLatest, autopilotStatus, autopilotToggle, autopilotRun, autopilotActivity, autopilotConfidence } from "../lib/api";
import { toast } from "sonner";
import { AgentBrain } from "./floor/AgentBrain";
import { FloorBoss } from "./FloorBoss";
import { HerculesCard } from "./HerculesCard";
import { MinyoCard } from "./MinyoCard";
import { EarningsFlowBoard } from "./EarningsFlowBoard";

const MARKETS = [
  { id: "crypto", label: "Crypto", icon: Bitcoin },
  { id: "stocks", label: "US Stocks", icon: LineChart },
  { id: "commodities", label: "Gold · Silver · Oil", icon: Gem },
  { id: "prediction", label: "Prediction (Kalshi)", icon: TrendingUp },
  { id: "forex", label: "Forex", icon: LineChart },
];

const AgentCard = ({ agent, idx, active }) => {
  const working = agent.status === "working";
  const done = agent.status === "done";
  return (
    <div className={`relative flex-1 min-w-[150px] rounded-2xl border p-3.5 transition-all duration-500
      ${working ? "border-[#FFD700] bg-[#FFD70010] scale-[1.03] shadow-[0_0_24px_#FFD70033]"
        : done ? "border-emerald-500/40 bg-emerald-500/5"
        : "border-[#ffffff14] bg-[#0a1020] opacity-60"}`} data-testid={`house-agent-${agent.id}`}>
      <div className="flex items-center gap-2">
        <AgentBrain agent={agent.id} size={36} active={working} dim={!working && !done} />
        <div className="min-w-0">
          <p className="text-cyan-50 font-bold text-sm leading-none truncate">{agent.name}</p>
          <p className="text-cyan-300/50 text-[10px] uppercase tracking-wider mt-0.5">{agent.role}</p>
        </div>
      </div>
      <div className="mt-2 min-h-[32px]">
        {done && (
          <div className="flex items-center gap-1.5 text-[11px]">
            <span className="text-cyan-300/50">{agent.in_count}</span>
            <ArrowRight className="w-3 h-3 text-cyan-300/30" />
            <span className="text-emerald-300 font-bold">{agent.out_count}</span>
            <span className="text-cyan-300/40">kept</span>
          </div>
        )}
        {(working || done) && agent.note && <p className="text-cyan-200/70 text-[11px] mt-1 leading-snug">{agent.note}</p>}
        {agent.status === "pending" && <p className="text-cyan-300/30 text-[11px]">waiting…</p>}
      </div>
      {idx < 5 && <div className="hidden lg:block absolute -right-2.5 top-1/2 -translate-y-1/2 text-cyan-300/20 z-10"><ArrowRight className="w-4 h-4" /></div>}
    </div>
  );
};

const WinnerCard = ({ idea, n }) => {
  const long = (idea.direction || "").toLowerCase() === "long";
  const jev = idea.jev;
  return (
    <div className="bg-[#060b18] border border-[#FFD70033] rounded-2xl p-4 space-y-2.5" data-testid="house-winner-card">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-cyan-50 font-bold flex items-center gap-2">
          <span className="text-[#FFD700]">{n}.</span>
          <span className="font-mono text-[#FFD700] text-lg">{idea.ticker}</span>
          <span className="text-cyan-200/70 font-normal text-sm">{idea.name || idea.company}</span>
        </h4>
        <span className={`px-2.5 py-1 rounded-full text-xs font-bold border flex items-center gap-1 ${long ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" : "text-rose-300 border-rose-500/40 bg-rose-500/10"}`}>
          {long ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}{idea.direction} · {idea.conviction || "High"}
        </span>
      </div>
      <p className="text-cyan-100/90 text-sm leading-relaxed">{idea.thesis}</p>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="bg-[#FFD70011] rounded-lg px-2 py-1.5"><p className="text-[#FFD700]/70">Entry</p><p className="text-cyan-50 font-semibold">{idea.entry}</p></div>
        <div className="bg-emerald-500/8 rounded-lg px-2 py-1.5"><p className="text-emerald-300/60">Target</p><p className="text-emerald-200 font-semibold">{idea.target}</p></div>
        <div className="bg-rose-500/8 rounded-lg px-2 py-1.5"><p className="text-rose-300/60">Stop</p><p className="text-rose-200 font-semibold">{idea.stop}</p></div>
      </div>
      {idea.catalyst && <p className="text-cyan-200/80 text-xs"><span className="text-[#FFD700] font-semibold">Catalyst:</span> {idea.catalyst}</p>}
      {idea.technicals && <p className="text-cyan-200/70 text-xs"><span className="text-cyan-300/60 font-semibold">Technicals:</span> {idea.technicals}</p>}
      <div className="flex items-center gap-2 pt-1 border-t border-[#ffffff0a]">
        <span className="text-[10px] text-cyan-300/40 flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-emerald-400" /> Passed all 5 agents</span>
        {jev?.action && (
          <span className={`ml-auto text-[11px] px-2 py-0.5 rounded-full font-bold border capitalize ${jev.action === "buy" ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" : "text-amber-300 border-amber-500/40 bg-amber-500/10"}`}>
            Mr Genie: {jev.action}{jev.action_confidence != null ? ` · ${Math.round(jev.action_confidence * 100)}%` : ""}
          </span>
        )}
      </div>
    </div>
  );
};

const StatusDot = ({ status }) => {
  const map = { placed: "bg-emerald-400", skipped: "bg-amber-400", error: "bg-rose-400" };
  return <span className={`inline-block w-2 h-2 rounded-full ${map[status] || "bg-cyan-400"}`} />;
};

const AutopilotActivity = ({ userId }) => {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { setData(await autopilotActivity(userId)); } catch (e) {}
  }, [userId]);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const orders = data?.orders || [];
  const sweeps = data?.sweeps || [];
  const positions = data?.open_positions || [];
  return (
    <div className="bg-[#060b18] border border-[#ffffff14] rounded-2xl p-4 space-y-3" data-testid="autopilot-activity">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-cyan-50 font-bold text-sm flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-300" /> Autopilot activity</p>
        <span className="text-[11px] text-cyan-300/50">live · refreshes every 15s</span>
        <span className="ml-auto text-[11px] text-cyan-200/70">Spent today: <b className="text-[#FFD700]">${(data?.spent_today ?? 0).toLocaleString()}</b> · {data?.placed_count || 0} placed · {positions.length} open</span>
      </div>

      {/* Sweeps */}
      {sweeps.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="autopilot-sweeps">
          {sweeps.map((s) => (
            <div key={s.id} className="shrink-0 rounded-xl border border-[#ffffff12] bg-[#0a1020] px-3 py-2 min-w-[150px]">
              <p className="text-[11px] text-cyan-300/50">{s.started_at ? new Date(s.started_at).toLocaleString() : ""}</p>
              <p className="text-xs text-cyan-100 font-semibold capitalize">{s.status}</p>
              <p className="text-[11px] text-emerald-300">{(s.results || []).reduce((a, r) => a + (r.winners || 0), 0)} winners · {(s.results || []).length}/{(s.markets || []).length} markets</p>
            </div>
          ))}
        </div>
      )}

      {/* Open positions */}
      {positions.length > 0 && (
        <div className="space-y-1">
          <p className="text-[11px] uppercase tracking-wider text-cyan-300/40">Open positions</p>
          {positions.slice(0, 8).map((p, i) => (
            <div key={i} className="flex items-center gap-2 text-xs bg-[#0a1020] rounded-lg px-2.5 py-1.5" data-testid="autopilot-position">
              <span className="font-bold text-cyan-50">{p.symbol}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${p.side === "short" || p.side === "no" ? "bg-rose-500/15 text-rose-300" : "bg-emerald-500/15 text-emerald-300"}`}>{(p.side || "").toUpperCase()}</span>
              <span className="text-cyan-300/50 capitalize">{p.market} · {p.venue}</span>
              <span className="ml-auto text-cyan-200/70">${(p.notional || 0).toLocaleString()}</span>
              {typeof p.pnl_pct === "number" && <span className={p.pnl_pct >= 0 ? "text-emerald-300" : "text-rose-300"}>{p.pnl_pct >= 0 ? "+" : ""}{p.pnl_pct.toFixed(1)}%</span>}
            </div>
          ))}
        </div>
      )}

      {/* Order feed */}
      <div className="space-y-1">
        <p className="text-[11px] uppercase tracking-wider text-cyan-300/40">Recent orders</p>
        {orders.length === 0 ? (
          <p className="text-cyan-200/40 text-xs py-3 text-center">No orders yet — the first picks will show here after the next sweep.</p>
        ) : (
          <div className="max-h-72 overflow-y-auto space-y-1">
            {orders.map((o, i) => (
              <div key={i} className="flex items-center gap-2 text-xs bg-[#0a1020] rounded-lg px-2.5 py-1.5" data-testid="autopilot-order">
                <StatusDot status={o.status} />
                <span className="font-bold text-cyan-50">{o.symbol || "—"}</span>
                <span className="text-cyan-300/50 uppercase text-[10px]">{o.direction}</span>
                <span className="text-cyan-300/40 capitalize text-[10px]">{o.market}{o.broker && o.broker !== "-" ? ` · ${o.broker}` : ""}</span>
                {o.notional > 0 && <span className="text-cyan-200/70">${o.notional.toLocaleString()}</span>}
                <span className={`text-[10px] font-semibold ${o.status === "placed" ? "text-emerald-300" : o.status === "error" ? "text-rose-300" : "text-amber-300"}`}>{o.status}</span>
                {o.error && <span className="text-rose-300/60 text-[10px] truncate max-w-[180px]" title={o.error}>{o.error}</span>}
                <span className="ml-auto text-cyan-300/30 text-[10px]">{o.created_at ? new Date(o.created_at).toLocaleTimeString() : ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default function NexusTradingHouse({ session }) {
  const userId = session.user_id;
  const [market, setMarket] = useState("crypto");
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ap, setAp] = useState(null);
  const [apBusy, setApBusy] = useState(false);
  const [conf, setConf] = useState(75);
  const pollRef = useRef(null);

  const loadAp = useCallback(async () => {
    try { const r = await autopilotStatus(userId); setAp(r); if (typeof r?.min_confidence === "number") setConf(Math.round(r.min_confidence)); } catch (e) {}
  }, [userId]);
  useEffect(() => { loadAp(); const t = setInterval(loadAp, 30000); return () => clearInterval(t); }, [loadAp]);

  const saveConf = async (val) => {
    setConf(val);
    try { const r = await autopilotConfidence(userId, val); setAp(r); toast.success(`Probability gate set to ${val}% — trades fire at or above this`); }
    catch (e) { toast.error("Couldn't update gate"); }
  };

  const toggleAp = async () => {
    setApBusy(true);
    try {
      const next = !ap?.enabled;
      const r = await autopilotToggle(userId, next);
      setAp(r);
      toast.success(next ? "Autopilot ON — the House now trades every market 24/7" : "Autopilot paused");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't toggle autopilot"); }
    finally { setApBusy(false); }
  };

  const sweepNow = async () => {
    try { await autopilotRun(userId); toast.success("Full sweep started — running every market now"); setTimeout(loadAp, 2000); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start sweep"); }
  };

  const load = useCallback(async (mkt) => {
    try { const r = await houseLatest(userId, mkt); setRun(r && r.id ? r : null); return r; } catch (e) { return null; }
  }, [userId]);
  useEffect(() => { load(market); }, [load, market]);

  useEffect(() => {
    const proc = run?.status === "processing";
    if (proc && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const r = await houseLatest(userId, run.market);
        setRun(r && r.id ? r : null);
        if (r?.status !== "processing") { clearInterval(pollRef.current); pollRef.current = null; }
      }, 4000);
    }
    return () => { if (pollRef.current && run?.status !== "processing") { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [run, userId]);

  const start = async () => {
    setBusy(true);
    try { const r = await houseRun(userId, market); setRun(r); toast.success("The House is in session — your 5 agents are on it"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start the House"); }
    finally { setBusy(false); }
  };

  const processing = run?.status === "processing";
  const agents = run?.agents || HOUSE_FALLBACK;
  const winners = run?.winners || [];

  return (
    <div className="h-full overflow-y-auto bg-[#030712] p-4 sm:p-6" data-testid="house-panel"
      style={{ backgroundImage: "radial-gradient(circle at 20% 0%, #FFD70008, transparent 40%)" }}>
      <div className="max-w-4xl mx-auto space-y-5">
        <div>
          <h2 className="text-[#FFD700] font-bold text-2xl flex items-center gap-2"><Building2 className="w-6 h-6" /> A V Trading House</h2>
          <p className="text-cyan-200/60 text-sm mt-1">Six expert agents vet every idea in sequence — only trades that survive <b>all of them</b> reach you. <span className="text-cyan-300/50">Scott → Jimmy → Prakash → Crams → London vs Tariq (+ Jimmy Cool) → Genie.</span></p>
        </div>

        {/* Autopilot master switch */}
        <div className={`rounded-2xl p-4 border transition-colors ${ap?.enabled ? "border-emerald-500/40 bg-emerald-500/5" : "border-[#ffffff14] bg-[#0a1020]"}`} data-testid="autopilot-banner">
          <div className="flex items-center gap-3 flex-wrap">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${ap?.enabled ? "bg-emerald-500/20 text-emerald-300" : "bg-[#ffffff0d] text-cyan-200/50"}`}>
              <Zap className={`w-6 h-6 ${ap?.enabled ? "animate-pulse" : ""}`} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-cyan-50 font-bold text-base flex items-center gap-2">
                Autopilot
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-bold ${ap?.enabled ? "bg-emerald-500/20 text-emerald-300" : "bg-[#ffffff10] text-cyan-200/50"}`}>
                  {ap?.enabled ? "ACTIVE · 24/7" : "PAUSED"}
                </span>
              </p>
              <p className="text-cyan-200/55 text-xs mt-0.5">
                {ap?.enabled
                  ? <>The House runs itself every hour across <b>all markets</b> — crypto, stocks, prediction, commodities & forex — auto-executing only ≥ your confidence gate. You don't press anything.</>
                  : <>Turn this on and the House trades every market on its own, all day. No manual runs.</>}
              </p>
            </div>
            <button onClick={toggleAp} disabled={apBusy} data-testid="autopilot-toggle"
              className={`px-4 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 disabled:opacity-50 border ${ap?.enabled ? "bg-emerald-500/90 hover:bg-emerald-500 text-white border-emerald-400" : "bg-[#FFD700] hover:bg-yellow-300 text-black border-[#FFD700]"}`}>
              {apBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />}
              {ap?.enabled ? "Turn OFF" : "Turn ON"}
            </button>
          </div>
          {ap?.enabled && (
            <div className="mt-3 pt-3 border-t border-[#ffffff12] flex items-center gap-2 flex-wrap text-[11px]">
              <span className="text-cyan-300/50">Markets:</span>
              {(ap.markets || []).map((m) => <span key={m} className="px-2 py-0.5 rounded-full bg-[#ffffff0d] text-cyan-200/70 capitalize">{m}</span>)}
              {ap.last && (
                <span className="text-cyan-300/40 ml-auto">
                  Last sweep: {ap.last.status}{ap.last.results ? ` · ${ap.last.results.reduce((a, r) => a + (r.winners || 0), 0)} winners` : ""} · {ap.last.started_at ? new Date(ap.last.started_at).toLocaleTimeString() : ""}
                </span>
              )}
              <button onClick={sweepNow} data-testid="autopilot-sweep-now" className="text-emerald-300/80 hover:text-emerald-200 underline">Sweep all now</button>
            </div>
          )}
          {/* Probability gate control */}
          <div className="mt-3 pt-3 border-t border-[#ffffff12]" data-testid="autopilot-confidence">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-cyan-200/70 text-xs font-semibold">Probability gate</span>
              <span className="text-[#FFD700] font-bold text-sm" data-testid="conf-value">{conf}%</span>
              <span className="text-cyan-300/40 text-[11px]">trades only fire at or above this confidence</span>
              <div className="flex gap-1 ml-auto">
                {[50, 60, 65, 70, 75, 80].map((v) => (
                  <button key={v} onClick={() => saveConf(v)} data-testid={`conf-preset-${v}`}
                    className={`px-2 py-1 rounded-lg text-[11px] font-bold border ${conf === v ? "bg-[#FFD700] text-black border-[#FFD700]" : "text-cyan-200/70 border-[#ffffff14] hover:bg-[#ffffff08]"}`}>{v}</button>
                ))}
              </div>
            </div>
            <input type="range" min="50" max="90" step="1" value={conf}
              onChange={(e) => setConf(Number(e.target.value))} onMouseUp={(e) => saveConf(Number(e.target.value))} onTouchEnd={(e) => saveConf(Number(e.target.value))}
              data-testid="conf-slider" className="w-full mt-2 accent-[#FFD700]" />
            <div className="flex justify-between text-[10px] text-cyan-300/30"><span>50% · aggressive, more trades</span><span>90% · ultra-selective</span></div>
          </div>
        </div>

        <FloorBoss />
        <HerculesCard />
        <MinyoCard />
        <EarningsFlowBoard />
        {ap?.enabled && <AutopilotActivity userId={userId} />}

        <div className="bg-[#060b18] border border-[#ffffff14] rounded-2xl p-3 space-y-2">
          <p className="text-cyan-300/50 text-[11px] uppercase tracking-wider flex items-center gap-1.5">
            <Microscope className="w-3.5 h-3.5" /> Manual single-market run (optional)
            <span className="normal-case tracking-normal text-cyan-300/40">· Autopilot already runs all {MARKETS.length} of these together automatically — use this only to spot-check one market now</span>
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex gap-1.5 flex-wrap">
              {MARKETS.map((m) => {
                const Icon = m.icon;
                return (
                  <button key={m.id} onClick={() => setMarket(m.id)} disabled={processing} data-testid={`house-market-${m.id}`}
                    className={`px-3 py-2 rounded-xl text-sm font-semibold flex items-center gap-1.5 transition-colors border
                      ${market === m.id ? "bg-[#FFD700] text-black border-[#FFD700]" : "text-cyan-200/70 border-[#ffffff14] hover:bg-[#ffffff08]"}`}>
                    <Icon className="w-4 h-4" />{m.label}
                  </button>
                );
              })}
            </div>
            <button onClick={start} disabled={busy || processing} data-testid="house-run-btn"
              className="ml-auto px-4 py-2.5 rounded-xl text-sm font-bold text-cyan-100 bg-[#ffffff12] hover:bg-[#ffffff20] disabled:opacity-50 flex items-center gap-2 border border-[#ffffff14]">
              {processing ? <><Loader2 className="w-4 h-4 animate-spin" /> In session…</> : <><Sparkles className="w-4 h-4" /> Run {MARKETS.find((m) => m.id === market)?.label || "this"} only</>}
            </button>
          </div>
        </div>

        {run && (
          <div className="space-y-4">
            <div className="flex flex-col lg:flex-row gap-3 lg:gap-5" data-testid="house-agents">
              {agents.map((a, i) => <AgentCard key={a.id} agent={a} idx={i} active={processing} />)}
            </div>

            {run.status === "error" && (
              <div className="bg-rose-500/5 border border-rose-500/30 rounded-2xl p-4 flex items-start gap-3" data-testid="house-error">
                <AlertTriangle className="w-5 h-5 text-rose-300 shrink-0 mt-0.5" />
                <p className="text-rose-200 text-sm">{run.error || "The House hit a snag — try again."}</p>
              </div>
            )}

            {run.status === "done" && (
              winners.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-emerald-300 text-sm font-semibold flex items-center gap-2" data-testid="house-winners-header">
                    <BadgeCheck className="w-4 h-4" /> {winners.length} trade{winners.length > 1 ? "s" : ""} cleared all 5 agents
                  </p>
                  {winners.map((idea, i) => <WinnerCard key={i} idea={idea} n={i + 1} />)}
                </div>
              ) : (
                <div className="text-center py-10 text-cyan-200/50" data-testid="house-no-winners">
                  <ShieldCheck className="w-9 h-9 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No trade survived all five agents this round — <b>that's the House protecting you</b>. Discipline over FOMO. Try another market or run again later.</p>
                </div>
              )
            )}
          </div>
        )}

        {!run && !processing && (
          <div className="text-center py-16 text-cyan-200/40" data-testid="house-empty">
            <Building2 className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="text-sm">Pick a market and <b>Run the House</b>. Watch Mr Scott, Mr Jimmy, Mr Prakash, Mr Crams & Mr Genie vet every idea live.</p>
          </div>
        )}
      </div>
    </div>
  );
}

const HOUSE_FALLBACK = [
  { id: "scott", name: "Mr Scott", role: "Scout", status: "pending", in_count: 0, out_count: 0, note: "" },
  { id: "jimmy", name: "Mr Jimmy", role: "Analyst", status: "pending", in_count: 0, out_count: 0, note: "" },
  { id: "prakash", name: "Mr Prakash", role: "Technician", status: "pending", in_count: 0, out_count: 0, note: "" },
  { id: "crams", name: "Mr Crams", role: "Validator", status: "pending", in_count: 0, out_count: 0, note: "" },
  { id: "debate", name: "London vs Tariq", role: "Bull / Bear + Jimmy Cool (Risk)", status: "pending", in_count: 0, out_count: 0, note: "" },
  { id: "genie", name: "Mr Genie", role: "Confirmer", status: "pending", in_count: 0, out_count: 0, note: "" },
];
